/**
 * Cart Context
 *
 * Provides shopping cart state and methods to the entire app
 */

import React, { createContext, useState, useEffect, useCallback, useContext } from 'react';
import { cartService } from '../services/cartService';
import { AuthContext } from './AuthContext';

export const CartContext = createContext(null);

export const CartProvider = ({ children }) => {
  // Get auth context to coordinate cart fetching
  const authContext = useContext(AuthContext);

  const [cart, setCart] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Derived state - handle different response formats from backend
  // itemCount should be total quantity, not number of unique items
  const itemCount = cart?.summary?.itemCount ||
                    cart?.items?.reduce((sum, item) => sum + (item.quantity || 1), 0) || 0;
  const uniqueItemCount = cart?.items?.length || 0;
  const subtotal = cart?.summary?.subtotal || cart?.subtotal || 0;
  const total = cart?.summary?.total || cart?.total || 0;
  const isEmpty = uniqueItemCount === 0;

  // Debug cart state (only in development)
  if (process.env.NODE_ENV === 'development') {
    // Removed verbose logging - cart state can be viewed in React DevTools
  }

  /**
   * Fetch cart from API
   * Works with both authenticated users and guest sessions
   * Returns { success: boolean, cart: object } for use in checkout flow
   */
  const fetchCart = useCallback(async () => {
    // Only fetch if we have a session ID or auth token
    const hasSession = localStorage.getItem('cartSessionId');
    const hasAuth = localStorage.getItem('authToken');

    if (!hasSession && !hasAuth) {
      // No session yet - set empty cart (will be created when first item is added)
      const emptyCart = { items: [], summary: { subtotal: 0, total: 0, itemCount: 0 } };
      setCart(emptyCart);
      return { success: true, cart: emptyCart };
    }

    try {
      setLoading(true);
      setError(null);

      if (process.env.NODE_ENV === 'development') {
        console.log('[CartContext] Fetching cart - session:', !!hasSession, 'auth:', !!hasAuth);
      }

      const response = await cartService.getCart();

      if (response.success && response.data) {
        if (process.env.NODE_ENV === 'development') {
          console.log('[CartContext] Cart fetched - items:', response.data.items?.length || 0);
        }
        setCart(response.data);
        return { success: true, cart: response.data };
      } else {
        throw new Error(response.error?.message || 'Failed to fetch cart');
      }
    } catch (error) {
      console.error('[CartContext] Fetch cart error:', error);

      const status = error.response?.status;
      const isNetworkError = !error.response;

      // Determine if we should clear the cart or keep it
      let shouldClearCart = false;
      let userMessage = '';

      if (status === 404) {
        // Cart session not found on backend - this is expected for new sessions
        shouldClearCart = true;
        userMessage = 'Cart session not found';
      } else if (status === 410) {
        // Cart session explicitly expired (if backend sends this)
        shouldClearCart = true;
        userMessage = 'Your cart session has expired';
        // Also clear the session ID
        localStorage.removeItem('cartSessionId');
      } else if (isNetworkError) {
        // Network error - keep existing cart, user can retry
        if (process.env.NODE_ENV === 'development') {
          console.warn('[CartContext] Network error, keeping cart');
        }
        shouldClearCart = false;
        userMessage = 'Unable to connect. Please check your internet connection.';

        setError(userMessage);
        // Keep the current cart state, don't set to empty
        return { success: false, cart: cart, error: userMessage };
      } else if (status >= 500) {
        // Server error - keep existing cart, might be temporary
        if (process.env.NODE_ENV === 'development') {
          console.warn('[CartContext] Server error, keeping cart');
        }
        shouldClearCart = false;
        userMessage = 'Our servers are experiencing issues. Please try again in a moment.';

        setError(userMessage);
        return { success: false, cart: cart, error: userMessage };
      } else {
        // Other errors - clear cart
        shouldClearCart = true;
        userMessage = error.response?.data?.message || 'Unable to load cart';
      }

      setError(userMessage);

      if (shouldClearCart) {
        const emptyCart = { items: [], summary: { subtotal: 0, total: 0, itemCount: 0 } };
        setCart(emptyCart);
        return { success: false, cart: emptyCart, error: userMessage };
      } else {
        // Return existing cart state
        return { success: false, cart: cart, error: userMessage };
      }
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch cart after auth is ready and when auth state changes
  useEffect(() => {
    // Wait for auth to finish initializing
    if (authContext && !authContext.initialized) {
      if (process.env.NODE_ENV === 'development') {
        console.log('[CartContext] Waiting for auth initialization...');
      }
      return;
    }

    // Additional safety check - wait for loading to complete
    if (authContext && authContext.loading) {
      if (process.env.NODE_ENV === 'development') {
        console.log('[CartContext] Waiting for auth...');
      }
      return;
    }

    if (process.env.NODE_ENV === 'development') {
      console.log('[CartContext] Auth ready, fetching cart');
    }
    fetchCart();
  }, [fetchCart, authContext]);

  /**
   * Add item to cart
   */
  const addToCart = useCallback(async (productId, quantity = 1, personalization = null) => {
    try {
      setLoading(true);
      setError(null);

      const response = await cartService.addToCart({
        productId,
        quantity,
        personalization
      });

      if (response.success && response.data) {
        setCart(response.data);
        return { success: true, cart: response.data };
      } else {
        throw new Error(response.error?.message || 'Failed to add to cart');
      }
    } catch (error) {
      console.error('[CartContext] Add to cart error:', error);

      // User-friendly error message
      const isNetworkError = !error.response;
      const status = error.response?.status;

      let userMessage = 'Unable to add item to cart';

      if (isNetworkError) {
        userMessage = 'Unable to connect. Please check your internet connection and try again.';
      } else if (status === 400) {
        userMessage = error.response?.data?.message || 'Invalid item or quantity';
      } else if (status === 404) {
        userMessage = 'Product not found';
      } else if (status === 409) {
        userMessage = 'This item is already in your cart';
      } else if (status >= 500) {
        userMessage = 'Our servers are experiencing issues. Please try again in a moment.';
      } else if (error.response?.data?.message) {
        userMessage = error.response.data.message;
      }

      setError(userMessage);
      return { success: false, error: userMessage };
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Update cart item quantity
   */
  const updateQuantity = useCallback(async (itemId, quantity) => {
    try {
      setLoading(true);
      setError(null);

      const response = await cartService.updateQuantity(itemId, quantity);

      if (response.success && response.data) {
        setCart(response.data);
        return { success: true, cart: response.data };
      } else {
        throw new Error(response.error?.message || 'Failed to update quantity');
      }
    } catch (error) {
      console.error('[CartContext] Update quantity error:', error);
      const errorMessage = error.message || 'Failed to update quantity';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Update cart item
   */
  const updateCartItem = useCallback(async (itemId, updates) => {
    try {
      setLoading(true);
      setError(null);

      const response = await cartService.updateCartItem(itemId, updates);

      if (response.success && response.data) {
        setCart(response.data);
        return { success: true, cart: response.data };
      } else {
        throw new Error(response.error?.message || 'Failed to update item');
      }
    } catch (error) {
      console.error('[CartContext] Update cart item error:', error);
      const errorMessage = error.message || 'Failed to update item';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Remove item from cart
   * Backend now returns updated cart state directly (stale data issue fixed)
   */
  const removeFromCart = useCallback(async (itemId) => {
    try {
      setLoading(true);
      setError(null);

      const response = await cartService.removeFromCart(itemId);

      if (response.success && response.data) {
        setCart(response.data);
        return { success: true, cart: response.data };
      } else {
        throw new Error(response.error?.message || 'Failed to remove item');
      }
    } catch (error) {
      console.error('[CartContext] Remove from cart error:', error);

      // User-friendly error message
      const isNetworkError = !error.response;
      const status = error.response?.status;

      let userMessage = 'Unable to remove item from cart';

      if (isNetworkError) {
        userMessage = 'Unable to connect. Please check your internet connection and try again.';
      } else if (status === 404) {
        userMessage = 'Item not found in cart';
      } else if (status >= 500) {
        userMessage = 'Our servers are experiencing issues. Please try again in a moment.';
      } else if (error.response?.data?.message) {
        userMessage = error.response.data.message;
      }

      setError(userMessage);
      return { success: false, error: userMessage };
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Clear cart
   */
  const clearCart = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await cartService.clearCart();

      if (response.success && response.data) {
        setCart(response.data);
        return { success: true, cart: response.data };
      } else {
        throw new Error(response.error?.message || 'Failed to clear cart');
      }
    } catch (error) {
      console.error('[CartContext] Clear cart error:', error);
      const errorMessage = error.message || 'Failed to clear cart';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Add personalization to cart item
   */
  const addPersonalization = useCallback(async (itemId, personalization) => {
    try {
      setLoading(true);
      setError(null);

      const response = await cartService.addPersonalization(itemId, personalization);

      if (response.success && response.data) {
        setCart(response.data);
        return { success: true, cart: response.data };
      } else {
        throw new Error(response.error?.message || 'Failed to add personalization');
      }
    } catch (error) {
      console.error('[CartContext] Add personalization error:', error);
      const errorMessage = error.message || 'Failed to add personalization';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Merge guest cart on login
   * Called when user logs in to refetch cart (backend merges automatically)
   *
   * Flow:
   * 1. Frontend sends GET /api/cart with both Authorization and X-Session-Id headers
   * 2. Backend detects both headers, merges guest cart into user cart
   * 3. Backend returns merged cart with X-Cart-Merge-Attempted header
   * 4. api.js interceptor clears session ID when merge header detected
   */
  const mergeGuestCart = useCallback(async () => {
    const sessionId = localStorage.getItem('cartSessionId');

    if (process.env.NODE_ENV === 'development') {
      console.log('[CartContext] Merging guest cart on login', {
        hasSessionId: !!sessionId,
        sessionIdPrefix: sessionId ? sessionId.substring(0, 8) + '...' : null
      });
    }

    // Fetch cart - backend auto-merges when both headers present
    // Session ID cleanup is handled in api.js response interceptor
    const result = await fetchCart();

    if (process.env.NODE_ENV === 'development') {
      console.log('[CartContext] Cart merge result', {
        success: result.success,
        itemCount: result.cart?.items?.length || 0,
        sessionIdCleared: !localStorage.getItem('cartSessionId')
      });
    }

    return result;
  }, [fetchCart]);

  /**
   * Clear error
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  /**
   * Check if product is in cart
   */
  const isInCart = useCallback((productId) => {
    if (!cart?.items) return false;
    return cart.items.some(item => item.productId === productId);
  }, [cart]);

  /**
   * Get cart item by product ID
   */
  const getCartItem = useCallback((productId) => {
    if (!cart?.items) return null;
    return cart.items.find(item => item.productId === productId) || null;
  }, [cart]);

  const value = {
    // State
    cart,
    loading,
    error,
    isEmpty,
    itemCount,        // Total quantity of all items
    uniqueItemCount,  // Number of unique products in cart
    subtotal,
    total,

    // Methods
    fetchCart,
    addToCart,
    updateQuantity,
    updateCartItem,
    removeFromCart,
    clearCart,
    addPersonalization,
    mergeGuestCart,
    clearError,
    isInCart,
    getCartItem
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

export default CartProvider;
